/**
 * Parse a prediction range string to lower and upper bounds
 * Examples:
 *   "0-100k" -> { lowerBound: 0, upperBound: 100000 }
 *   "100k-500k" -> { lowerBound: 100000, upperBound: 500000 }
 *   "500k-1M" -> { lowerBound: 500000, upperBound: 1000000 }
 *   "1M+" -> { lowerBound: 1000000, upperBound: Number.MAX_SAFE_INTEGER }
 */
export function parseRange(range: string): { lowerBound: bigint; upperBound: bigint } {
  const parseValue = (value: string): number => {
    value = value.trim().toUpperCase();

    if (value.endsWith('M')) {
      return parseFloat(value.slice(0, -1)) * 1_000_000;
    } else if (value.endsWith('K')) {
      return parseFloat(value.slice(0, -1)) * 1_000;
    } else if (value === '+') {
      return Number.MAX_SAFE_INTEGER;
    }

    return parseFloat(value);
  };

  // Handle "X+" format (e.g., "1M+")
  if (range.includes('+')) {
    const value = range.replace('+', '').trim();
    return {
      lowerBound: BigInt(parseValue(value)),
      upperBound: BigInt(Number.MAX_SAFE_INTEGER),
    };
  }

  // Handle "X-Y" format (e.g., "0-100k", "100k-500k")
  const parts = range.split('-');
  if (parts.length !== 2) {
    throw new Error(`Invalid range format: ${range}`);
  }

  return {
    lowerBound: BigInt(parseValue(parts[0]) === 0 ? 1 : parseValue(parts[0])),
    upperBound: BigInt(parseValue(parts[1])),
  };
}

/**
 * Format a number to a human-readable string with k/M suffix
 */
export function formatNumber(num: number | bigint): string {
  const n = typeof num === 'bigint' ? Number(num) : num;

  if (n >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(1)}M`;
  } else if (n >= 1_000) {
    return `${(n / 1_000).toFixed(1)}k`;
  }

  return n.toString();
}
